import { config } from '@/config/config';
import type { ApiResponse, ApiRequestConfig } from '@/types/api';
import { getStoredTokens } from '@/lib/auth';
import { getStoredOrganizerTokens } from '@/lib/organizer-auth';
import type { AuthTokens } from '@/types/auth';

export class ApiService {
  private static instance: ApiService;
  private baseUrl: string;
  private timeout: number;
  private token: string | null = null;
  private firebaseToken: string | null = null;
  private refreshingPromise: Promise<AuthTokens | null> | null = null;

  private constructor() {
    this.baseUrl = config.api.url;
    this.timeout = config.api.timeout;
    this.initializeTokenFromStorage();
  }

  static getInstance(): ApiService {
    if (!ApiService.instance) {
      ApiService.instance = new ApiService();
    }
    return ApiService.instance;
  }

  /**
   * Inicializa el token desde el almacenamiento local al cargar la aplicación
   */
  private initializeTokenFromStorage(): void {
    const tokens = getStoredTokens();
    if (tokens && tokens.accessToken) {
      this.token = tokens.accessToken;
      this.firebaseToken = tokens.firebaseToken || null;
    }
  }

  setToken(token: string, firebaseToken: string) {
    this.token = token;
    this.firebaseToken = firebaseToken;
  }

  clearToken() {
    this.token = null;
    this.firebaseToken = null;
  }

  private getHeaders(security: boolean = false, async: boolean = true, file: boolean = false): HeadersInit {
    const headers: HeadersInit = {
      'Accept': 'application/json',
    };

    if (!file) {
      headers['Content-Type'] = 'application/json';
    }

    if (async) {
      headers['X-Requested-With'] = 'XMLHttpRequest';
    }

    if (security) {
      if (this.token) {
        headers['Authorization'] = `Bearer ${this.token}`;
      } else {
        console.warn('[ApiService] Security=true but NO JWT token available!');
      }
    }

    if (security && this.firebaseToken) {
      headers['X-Firebase-Token'] = this.firebaseToken;
    }

    // Fallback: si no hay Authorization (ni con security ni sin él), añade el JWT de
    // exchange-manager-token cuando el organizador está en modo preview. Permite al BE
    // detectar bypass de inscripción (tarifas ocultas, sin pago, sin obligatorias) en
    // endpoints públicos del microsite sin tocar cada caller.
    if (!headers['Authorization']) {
      const organizerTokens = getStoredOrganizerTokens();
      if (organizerTokens?.accessToken) {
        headers['Authorization'] = `Bearer ${organizerTokens.accessToken}`;
      }
    }

    // Always include API Key for anonymous access
    // Backend will prioritize JWT if valid, otherwise will use API Key
    // ALWAYS send API Key as fallback (backend will use JWT if valid, API Key if not)
    if (config.api.apiKey) {
      headers['X-API-Key'] = config.api.apiKey;
    } else {
      console.warn('[ApiService] No API Key configured - requests may fail');
    }

    return headers;
  }

  /**
   * Método para refrescar el token utilizando el servicio de autenticación
   * Debe ser implementado por el servicio de autenticación para evitar dependencias circulares
   */
  private async refreshAccessToken(): Promise<AuthTokens | null> {
    // Si ya hay una operación de refresco en curso, esperamos a que termine
    if (this.refreshingPromise) {
      return this.refreshingPromise;
    }

    try {
      // Importar dinámicamente para evitar dependencia circular
      const { AuthService } = await import('./auth');
      const authService = AuthService.getInstance();

      // Guardar la promesa para que múltiples llamadas concurrentes usen la misma operación de refresco
      this.refreshingPromise = authService.refreshToken();
      const result = await this.refreshingPromise;
      return result;
    } catch (error) {
      console.error('Error en refreshAccessToken:', error);
      return null;
    } finally {
      // Reset la promesa cuando termine, ya sea con éxito o error
      this.refreshingPromise = null;
    }
  }

  async request<T>(config: ApiRequestConfig, retryCount = 0): Promise<ApiResponse<T>> {
    try {
      const { path, method = 'GET', body, security = false, async = true, file = false, extraHeaders } = config;

      const baseHeaders = this.getHeaders(security, async, file);

      const allHeaders = { ...baseHeaders, ...(extraHeaders || {}) };

      // Use shorter timeout for first attempt to fail fast on bad pods
      const attemptTimeout = retryCount === 0 ? Math.min(this.timeout, 12000) : this.timeout;

      const requestConfig = {
        method,
        headers: allHeaders,
        body: file ? body as BodyInit : (body ? JSON.stringify(body) : null),
        signal: AbortSignal.timeout(attemptTimeout),
      }

      const response = await fetch(`${this.baseUrl}${path}`, requestConfig);

      // Si recibimos un 401 y no estamos en un intento de refresco de token y no excedimos los reintentos
      if (response.status === 401 && security && retryCount < 1 &&
        !path.includes('/api/auth/refresh') && !path.includes('/api/auth/login')) {

        // Check if it's a TOKEN_REFRESH_REQUIRED error (old token without athleteId)
        try {
          const errorBody = await response.clone().json();
          if (errorBody?.code === 'TOKEN_REFRESH_REQUIRED') {
            console.warn('Token requires re-authentication. Forcing logout...');
            // Force logout - clear tokens and redirect to login
            this.clearToken();
            const { clearTokens } = await import('@/lib/auth');
            clearTokens();
            // Redirect to login page
            if (typeof window !== 'undefined') {
              window.location.href = '/login';
            }
            throw new Error('Session expired. Please login again.');
          }
        } catch (parseError) {
          // If we can't parse the error, continue with normal refresh flow
        }

        // Intentar refrescar el token
        const newTokens = await this.refreshAccessToken();

        if (newTokens) {
          // Si el refresco fue exitoso, actualizar el token y reintentar la petición original
          this.setToken(newTokens.accessToken, newTokens.firebaseToken);
          return this.request<T>(config, retryCount + 1);
        } else {
          console.error('Failed to refresh token');
          // Si el refresco falló, lanzar error 401
          throw new Error(`HTTP error! status: 401, details: Unauthorized and token refresh failed`);
        }
      }

      if (!response.ok && !requestConfig.signal.aborted) {
        let errorDetails = '';

        try {
          // Clonar la respuesta para poder leer el cuerpo múltiples veces si es necesario
          const responseClone = response.clone();
          const errorText = await responseClone.text();

          try {
            // Intentar parsear como JSON
            const errorJson = JSON.parse(errorText);
            errorDetails = JSON.stringify(errorJson);
          } catch {
            // Si no es JSON válido, usar el texto directamente
            errorDetails = errorText || 'No error details available';
          }
        } catch (parseError) {
          errorDetails = `Failed to parse error response: ${parseError}`;
        }

        // Enhanced logging for 500 errors
        if (response.status === 500) {
          console.error('HTTP 500 Error:', {
            url: `${this.baseUrl}${path}`,
            method,
            status: response.status,
            statusText: response.statusText,
            errorDetails
          });
        }

        throw new Error(`HTTP error! status: ${response.status}, details: ${errorDetails}`);
      }

      const data = await response.json();

      // Si la data es un array, envolverla en un objeto ApiResponse
      if (Array.isArray(data)) {
        return {
          items: data,
          statusCode: response.status
        };
      }

      return {
        ...data,
      };
    } catch (error) {
      // Auto-retry once on timeout/network errors (handles bad pod scenario in K8s)
      const isTimeoutOrNetwork = error instanceof Error && (
        error.name === 'TimeoutError' ||
        error.name === 'AbortError' ||
        error.message.includes('signal') ||
        error.message.includes('network') ||
        error.message.includes('fetch')
      );

      if (isTimeoutOrNetwork && retryCount < 1) {
        console.warn(`[ApiService] Request timeout/network error on ${config.path}, retrying...`);
        return this.request<T>(config, retryCount + 1);
      }

      const message = error instanceof Error ? error.message : 'Unknown error occurred';
      const statusMatch = message.match(/status:\s*(\d{3})/);
      const statusCode = statusMatch ? parseInt(statusMatch[1], 10) : 500;

      return {
        error: message,
        statusCode,
      };
    }
  }
}